import React, { useState, useEffect } from 'react';
import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Typography
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Bolt as BoltIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import Header from './Header';
import DashboardTab from './DashboardTab';
import ChargersTab from './ChargersTab';
import TransactionsTab from './TransactionsTab';
import ChargerDetailsDrawer from './ChargerDetailsDrawer';
import { useAccess } from '../../hooks/useAccess';

const baseTheme = createTheme();



// Modern theme with latest MUI v7 features
const theme = createTheme({
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: '#1976d2',
          light: '#42a5f5',
          dark: '#1565c0',
          contrastText: '#ffffff',
        },
        secondary: {
          main: '#ed6c02',
          light: '#ff9800',
          dark: '#e65100',
        },
        success: {
          main: '#2e7d32',
          light: '#4caf50',
          dark: '#1b5e20',
        },
        warning: {
          main: '#ed6c02',
          light: '#ff9800',
          dark: '#e65100',
        },
        error: {
          main: '#d32f2f',
          light: '#f44336',
          dark: '#c62828',
        },
        background: {
          default: '#fafafa',
          paper: '#ffffff',
        },
        surface: {
          main: '#ffffff',
          variant: '#f5f5f5',
        },
      },
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h6: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    body1: {
      fontSize: '0.95rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.85rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)',
    '0px 3px 6px rgba(0, 0, 0, 0.16), 0px 3px 6px rgba(0, 0, 0, 0.23)',
    '0px 10px 20px rgba(0, 0, 0, 0.19), 0px 6px 6px rgba(0, 0, 0, 0.23)',
    '0px 14px 28px rgba(0, 0, 0, 0.25), 0px 10px 10px rgba(0, 0, 0, 0.22)',
    '0px 19px 38px rgba(0, 0, 0, 0.30), 0px 15px 12px rgba(0, 0, 0, 0.22)',
    ...baseTheme.shadows.slice(6),
  ],
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          '&:hover': {
            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 20px',
          fontSize: '0.9rem',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.8rem',
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          backdropFilter: 'blur(20px)',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          height: 8,
        },
        bar: {
          borderRadius: 10,
        },
      },
    },
  },
});

const MobileDemo = () => {
  // Initialize activeTab from localStorage or default to 0
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('activeTab');
    return savedTab !== null ? parseInt(savedTab, 10) : 0;
  });
  const [selectedCharger, setSelectedCharger] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const canViewFleetops = useAccess('Fleetops', 'V');

  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab.toString());
  }, [activeTab]);

  useEffect(() => {
    if (!canViewFleetops) {
      setDetailsOpen(false);
      setSelectedCharger(null);
    }
  }, [canViewFleetops]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChargerDetails = (charger) => {
    setSelectedCharger(charger);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedCharger(null);
  };

  const handleRefresh = () => {
    // Force refresh of active tab content by updating refresh key
    setRefreshKey(prev => prev + 1);
    console.log('MobileDemo: Refreshing tab content');
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case 0: return 'Dashboard';
      case 1: return 'Chargers';
      case 2: return 'Transactions';
      default: return 'Zynka Charge';
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return <DashboardTab key={`dashboard-${refreshKey}`} />;
      case 1:
        return <ChargersTab key={`chargers-${refreshKey}`} onChargerDetails={handleChargerDetails} />;
      case 2:
        return <TransactionsTab key={`transactions-${refreshKey}`} />;
      default:
        return <DashboardTab key={`dashboard-${refreshKey}`} />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ 
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        bgcolor: 'background.default',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Fixed Header */}
        <Header title={getTabTitle()} />

        {/* Scrollable Main Content */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto',
          overflowX: 'hidden',
          pb: 7,
          position: 'relative'
        }}>
          {canViewFleetops ? (
            renderTabContent()
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Access restricted
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You dont have access of this application to use
              </Typography>
            </Box>
          )}
        </Box>

        {/* Bottom Navigation
            horizontal: 'right',
        {/* Bottom Navigation */}
        {canViewFleetops && (
          <BottomNavigation
            value={activeTab}
            onChange={handleTabChange}
            showLabels
          >
            <BottomNavigationAction
              label="Dashboard"
              icon={<DashboardIcon />}
            />
            <BottomNavigationAction
              label="Chargers"
              icon={<BoltIcon />}
            />
            <BottomNavigationAction
              label="Transactions"
              icon={<ReceiptIcon />}
            />
          </BottomNavigation>
        )}

        {/* Details Drawer */}
        {canViewFleetops && (
          <ChargerDetailsDrawer
            open={detailsOpen}
            onClose={handleCloseDetails}
            charger={selectedCharger}
            onRefresh={handleRefresh}
          />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default MobileDemo;